/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetRemoteSessionClientRequest,
  GetRemoteSessionClientSecurity,
} from "../models/operations/getremotesessionclient.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRemoteSessionClientQuery,
  prefetchRemoteSessionClient,
  queryKeyRemoteSessionClient,
  RemoteSessionClientQueryData,
} from "./remoteSessionClient.core.js";
export {
  buildRemoteSessionClientQuery,
  prefetchRemoteSessionClient,
  queryKeyRemoteSessionClient,
  type RemoteSessionClientQueryData,
};

export type RemoteSessionClientQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getRemoteSessionClient remoteSessionClients
 *
 * @remarks
 * Get a remote_session_client by id.
 */
export function useRemoteSessionClient(
  request: GetRemoteSessionClientRequest,
  security?: GetRemoteSessionClientSecurity | undefined,
  options?: QueryHookOptions<
    RemoteSessionClientQueryData,
    RemoteSessionClientQueryError
  >,
): UseQueryResult<RemoteSessionClientQueryData, RemoteSessionClientQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRemoteSessionClientQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getRemoteSessionClient remoteSessionClients
 *
 * @remarks
 * Get a remote_session_client by id.
 */
export function useRemoteSessionClientSuspense(
  request: GetRemoteSessionClientRequest,
  security?: GetRemoteSessionClientSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RemoteSessionClientQueryData,
    RemoteSessionClientQueryError
  >,
): UseSuspenseQueryResult<
  RemoteSessionClientQueryData,
  RemoteSessionClientQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRemoteSessionClientQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRemoteSessionClientData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RemoteSessionClientQueryData,
): RemoteSessionClientQueryData | undefined {
  const key = queryKeyRemoteSessionClient(...queryKeyBase);

  return client.setQueryData<RemoteSessionClientQueryData>(key, data);
}

export function invalidateRemoteSessionClient(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessionClients", "get", ...queryKeyBase],
  });
}

export function invalidateAllRemoteSessionClient(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessionClients", "get"],
  });
}
